<template>
    <BaseDialog
        :open="open"
        :disable-close-on-backdrop-click="isSaving"
        :show-close="!isSaving"
        title="Set your time zone"
        width="narrow"
        @update:open="emit('update:open', $event)">
        <form :id="formId" class="flex w-full flex-col gap-4" @submit.prevent="saveTimezone">
            <p class="body-small text-ink-muted">Set your time zone to get the full Development Journeys experience.</p>

            <label class="flex flex-col gap-2" for="development-journeys-timezone">
                <span class="body-small-semibold text-control-label">Time zone</span>
                <BaseCombobox
                    v-model="selectedTimezone"
                    :disabled="isSaving"
                    :options="timezoneOptions"
                    input-id="development-journeys-timezone"
                    placeholder="Select a time zone"
                    auto-focus />
            </label>

            <div role="alert" aria-atomic="true">
                <BaseNotification v-if="saveError" role="none" theme="error" :text="saveError" hide-dismiss />
            </div>
        </form>
        <template #footer>
            <JourneyButton :form="formId" type="submit" theme="primary" size="md" :disabled="!selectedTimezone || isSaving" data-testid="save-development-journeys-timezone">
                {{ isSaving ? "Saving…" : "Save and continue" }}
            </JourneyButton>
        </template>
    </BaseDialog>
</template>

<script setup lang="ts">
import { useFetch } from "~vue/api";
import BaseCombobox from "~vue/components/BaseCombobox.vue";
import BaseDialog from "~vue/components/BaseDialog.vue";
import BaseNotification from "~vue/components/BaseNotification.vue";
import JourneyButton from "~vue/pages/developmentJourneys/DevelopmentJourneyButton.vue";
import { computed, ref, useId, watch } from "vue";

const props = defineProps<{
    open: boolean;
    timezones: string[];
    timezone?: string;
}>();
const formId = useId();

const emit = defineEmits<{
    saved: [];
    "update:open": [open: boolean];
}>();

type TimezoneOption = {
    label: string;
    value: string;
};

const timezoneOptions = computed<TimezoneOption[]>(() => props.timezones.map((timezone) => ({ label: timezone, value: timezone })));
const selectedTimezone = ref<TimezoneOption | null>(null);
const saveError = ref<string | null>(null);
const timezonePayload = computed(() => ({ timezone: selectedTimezone.value?.value }));
const { error: timezoneUpdateError, execute: updateTimezone, isFetching: isSaving } = useFetch("/api/accounts/user", { immediate: false }).put(timezonePayload).json();

watch(
    () => [props.open, props.timezone],
    ([isOpen, timezone]) => {
        if (isOpen) {
            selectedTimezone.value = timezoneOptions.value.find((option) => option.value === timezone) ?? null;
        }
    },
    { immediate: true },
);

const saveTimezone = async () => {
    if (!selectedTimezone.value || isSaving.value) {
        return;
    }

    saveError.value = null;
    await updateTimezone();

    if (timezoneUpdateError.value) {
        saveError.value = "We couldn't save your time zone. Please try again.";
        return;
    }

    emit("saved");
};
</script>
